import {
  Bell,
  CornerDownRight,
  Hash,
  Lock,
  MessageCircle,
  MessageSquareText,
  Mic,
  Paperclip,
} from 'lucide-react';
import { Bone } from '@/components/ui/Skeleton';
import { Avatar } from '@/components/common/Avatar';
import type { ChannelData, ChannelMessageData } from '@/api/services/channels.service';
import {
  escapeRegExp,
  excerptAround,
  formatDuration,
  formatFullDate,
  formatResultDate,
  fullName,
  isDirectKind,
  senderOf,
  toPlainText,
} from '../utils/messageResults';

/** Building blocks shared by message search and saved messages. */

export function Highlighted({
  text,
  terms,
}: {
  text: string;
  terms: string[];
}): React.ReactElement {
  if (terms.length === 0) return <>{text}</>;
  const pattern = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'gi');
  return (
    <>
      {text.split(pattern).map((part, index) =>
        index % 2 === 1 ? (
          <mark
            key={index}
            className="rounded-[3px] bg-amber-100 px-0.5 text-slate-900 dark:bg-amber-400/20 dark:text-amber-100"
          >
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </>
  );
}

export function ConversationLabel({ channel }: { channel?: ChannelData }): React.ReactElement {
  if (!channel)
    return <span className="truncate text-slate-400 dark:text-gray-500">Conversation</span>;
  const Icon = isDirectKind(channel.kind)
    ? MessageCircle
    : channel.kind === 'announcement'
      ? Bell
      : channel.visibility === 'private'
        ? Lock
        : Hash;
  return (
    <span className="inline-flex min-w-0 items-center gap-1 text-slate-500 dark:text-gray-400">
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{channel.name}</span>
    </span>
  );
}

export function ResultIndicators({
  message,
}: {
  message: ChannelMessageData;
}): React.ReactElement | null {
  const voiceNotes = message.attachments.filter((file) => file.mimeType.startsWith('audio/'));
  const files = message.attachments.filter((file) => !file.mimeType.startsWith('audio/'));
  const items: React.ReactNode[] = [];
  if (message.threadRootId)
    items.push(
      <span key="thread" className="inline-flex items-center gap-1">
        <CornerDownRight className="h-3 w-3" />
        Thread reply
      </span>
    );
  else if (message.replyCount > 0)
    items.push(
      <span key="replies" className="inline-flex items-center gap-1">
        <MessageSquareText className="h-3 w-3" />
        {message.replyCount} {message.replyCount === 1 ? 'reply' : 'replies'}
      </span>
    );
  if (voiceNotes.length > 0) {
    const duration = voiceNotes[0].durationSeconds;
    items.push(
      <span key="voice" className="inline-flex items-center gap-1">
        <Mic className="h-3 w-3" />
        Voice note{duration ? ` · ${formatDuration(duration)}` : ''}
      </span>
    );
  }
  if (files.length > 0)
    items.push(
      <span key="files" className="inline-flex min-w-0 items-center gap-1">
        <Paperclip className="h-3 w-3 shrink-0" />
        <span className="truncate">
          {files.length === 1 ? files[0].name : `${files.length} files`}
        </span>
      </span>
    );
  if (items.length === 0) return null;
  return (
    <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-slate-500 dark:text-gray-400">
      {items}
    </div>
  );
}

/** Avatar, sender · conversation · time, a two-line excerpt and indicators. */
export function MessageResultContent({
  message,
  channel,
  terms = [],
}: {
  message: ChannelMessageData;
  channel?: ChannelData;
  terms?: string[];
}): React.ReactElement {
  const person = senderOf(message);
  const name = person ? fullName(person) : 'Former member';
  const text = toPlainText(message.content);
  return (
    <>
      <Avatar name={name} src={person?.avatar} size="md" className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5 text-[11px]">
          <span className="truncate font-semibold text-slate-900 dark:text-gray-100">{name}</span>
          <span className="shrink-0 text-slate-300 dark:text-gray-600">·</span>
          <ConversationLabel channel={channel} />
          <time
            dateTime={message.createdAt}
            title={formatFullDate(message.createdAt)}
            className="ml-auto shrink-0 pl-2 text-[10px] text-slate-400 dark:text-gray-500"
          >
            {formatResultDate(message.createdAt)}
          </time>
        </div>
        <p className="mt-0.5 line-clamp-2 break-words text-[12px] leading-[18px] text-slate-600 dark:text-gray-300">
          {text ? (
            <Highlighted text={excerptAround(text, terms)} terms={terms} />
          ) : (
            <span className="italic text-slate-400 dark:text-gray-500">No text</span>
          )}
        </p>
        <ResultIndicators message={message} />
      </div>
    </>
  );
}

export function ResultSkeleton(): React.ReactElement {
  return (
    <div aria-hidden className="space-y-1 px-1.5 py-1">
      {[0.92, 0.7, 0.84, 0.6, 0.78].map((width, index) => (
        <div key={index} className="flex gap-3 rounded-md px-2.5 py-2.5">
          <Bone className="h-8 w-8 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-2 pt-0.5">
            <div className="flex items-center gap-2">
              <Bone className="h-3 w-24 rounded" />
              <Bone className="h-3 w-16 rounded" />
              <Bone className="ml-auto h-2.5 w-12 rounded" />
            </div>
            <Bone className="h-2.5 rounded" style={{ width: `${width * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function StatePanel({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-400 dark:bg-white/[0.06] dark:text-gray-500">
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-[13px] font-semibold text-slate-800 dark:text-gray-100">{title}</p>
      {children && (
        <div className="mt-1 max-w-xs text-[12px] leading-5 text-slate-500 dark:text-gray-400">
          {children}
        </div>
      )}
    </div>
  );
}

export function Kbd({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <kbd className="inline-flex min-w-[18px] items-center justify-center rounded border border-slate-200 bg-slate-50 px-1 py-0.5 font-sans text-[10px] font-medium text-slate-500 dark:border-white/10 dark:bg-white/[0.06] dark:text-gray-400">
      {children}
    </kbd>
  );
}
